(() => {
  const SELECTOR = '[data-sold-out-topfold]';
  const EMAIL_SUBMITTED_STORAGE_KEY = 'daisySoldOutTopfoldEmailSubmitted';

  const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

  const normalizePhoneNumber = (value) => {
    const trimmed = String(value || '').trim();
    const hasInternationalPrefix = trimmed.startsWith('+') || trimmed.startsWith('00');
    const digits = trimmed.replace(/\D/g, '');

    if (!hasInternationalPrefix && digits.length === 10) return `+1${digits}`;
    if (!hasInternationalPrefix && digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
    if (trimmed.startsWith('00')) return `+${digits.slice(2)}`;
    if (trimmed.startsWith('+')) return `+${digits}`;
    return '';
  };

  const isValidPhoneNumber = (value) => /^\+[1-9]\d{7,14}$/.test(value);

  const markEmailSubmitted = () => {
    window.daisySoldOutTopfoldEmailSubmitted = true;
    try {
      sessionStorage.setItem(EMAIL_SUBMITTED_STORAGE_KEY, '1');
    } catch (_error) {
      // The in-memory flag still suppresses the popup when storage is unavailable.
    }
    document.dispatchEvent(new CustomEvent('daisy:sold-out-topfold-email-submitted'));
  };

  const buildSubscriptionPayload = ({ email, phoneNumber, listId, source }) => {
    const attributes = { email };

    if (phoneNumber) {
      attributes.phone_number = phoneNumber;
      attributes.subscriptions = {
        sms: {
          marketing: {
            consent: 'SUBSCRIBED'
          }
        }
      };
    } else {
      attributes.subscriptions = {
        email: {
          marketing: {
            consent: 'SUBSCRIBED'
          }
        }
      };
    }

    return {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: source,
          profile: {
            data: {
              type: 'profile',
              attributes
            }
          }
        },
        relationships: {
          list: {
            data: {
              type: 'list',
              id: listId
            }
          }
        }
      }
    };
  };

  const initializeTopfold = (topfold) => {
    if (topfold.dataset.soldOutInitialized === 'true') return;
    topfold.dataset.soldOutInitialized = 'true';

    const stage = topfold.querySelector('.sold-out-topfold__stage');
    const signup = topfold.querySelector('[data-sold-out-signup]');
    const form = topfold.querySelector('.sold-out-topfold__form');
    const heading = topfold.querySelector('[data-sold-out-heading]');
    const emailStep = topfold.querySelector('[data-sold-out-email-step]');
    const smsStep = topfold.querySelector('[data-sold-out-sms-step]');
    const smsConsent = topfold.querySelector('[data-sold-out-sms-consent]');
    const emailInput = form?.querySelector('input[type="email"]');
    const phoneInput = form?.querySelector('input[type="tel"]');
    const skipSmsButton = form?.querySelector('[data-sold-out-skip-sms]');
    const status = form?.querySelector('[data-sold-out-status]');
    const publicKey = topfold.dataset.klaviyoPublicKey || '';
    const listId = topfold.dataset.klaviyoListId || '';
    const revision = topfold.dataset.klaviyoRevision || '2026-07-15';
    const endpoint = `https://a.klaviyo.com/client/subscriptions?company_id=${encodeURIComponent(publicKey)}`;
    let currentStep = 'email';
    let submittedEmail = '';

    if (!stage || !signup || !form || !heading || !emailStep || !smsStep || !emailInput || !phoneInput || !status) {
      return;
    }

    const setScale = () => {
      const isMobile = window.matchMedia('(max-width: 767px)').matches;
      const viewportWidth = topfold.getBoundingClientRect().width || window.innerWidth;

      if (isMobile) {
        const frameWidth = 402;
        const frameHeight = 797;
        const scale = viewportWidth / frameWidth;
        const topCrop = 0;
        const completionBottomTrim = topfold.classList.contains('is-final-success') ? 55 : 0;

        topfold.style.setProperty('--sold-out-scale', String(scale));
        topfold.style.setProperty('--sold-out-stage-offset', `${-topCrop}px`);
        topfold.style.setProperty('height', `${(frameHeight - completionBottomTrim) * scale - topCrop}px`);
        topfold.style.setProperty('aspect-ratio', 'auto');
        return;
      }

      const frameWidth = 1496;
      const frameHeight = 967;
      const contentTop = 174;
      const contentHeight = 621;
      const viewportHeight = window.visualViewport?.height || window.innerHeight;
      const topfoldDocumentTop = topfold.getBoundingClientRect().top + window.scrollY;
      const availableHeight = Math.max(1, viewportHeight - Math.max(0, topfoldDocumentTop));
      const scale = Math.min(1, viewportWidth / frameWidth, availableHeight / contentHeight);
      const scaledFrameHeight = frameHeight * scale;
      const renderedHeight = Math.min(scaledFrameHeight, availableHeight);
      const stageOffset = scaledFrameHeight > renderedHeight
        ? ((renderedHeight - contentHeight * scale) / 2) - contentTop * scale
        : 0;

      topfold.style.setProperty('--sold-out-scale', String(scale));
      topfold.style.setProperty('--sold-out-stage-offset', `${stageOffset}px`);
      topfold.style.setProperty('height', `${renderedHeight}px`);
      topfold.style.setProperty('aspect-ratio', 'auto');
    };

    const setBusy = (busy) => {
      form.querySelectorAll('button[type="submit"]').forEach((button) => {
        button.disabled = busy;
      });
      form.setAttribute('aria-busy', String(busy));
    };

    const setStatus = (message, isError = false) => {
      status.textContent = message;
      status.classList.toggle('is-error', isError);
    };

    const showEmailConfirmedStatus = () => {
      const checkmark = document.createElement('span');
      checkmark.className = 'sold-out-topfold__confirmed-check';
      checkmark.setAttribute('aria-hidden', 'true');
      checkmark.textContent = '✓';

      status.replaceChildren(document.createTextNode('email confirmed '), checkmark);
      status.classList.remove('is-error');
    };

    const showSmsStep = () => {
      currentStep = 'sms';
      emailStep.hidden = true;
      smsStep.hidden = false;
      smsConsent.hidden = false;
      heading.textContent = window.matchMedia('(max-width: 767px)').matches
        ? 'enter your phone number for first access to the restock'
        : 'enter your phone number to not miss the restock';
      showEmailConfirmedStatus();
      window.setTimeout(() => phoneInput.focus({ preventScroll: true }), 0);
    };

    const showComplete = (message, centered = false) => {
      currentStep = 'complete';
      emailStep.hidden = true;
      smsStep.hidden = true;
      smsConsent.hidden = true;
      heading.textContent = message;
      signup.classList.toggle('is-final-success', centered);
      topfold.classList.toggle('is-final-success', centered);
      setScale();
      setStatus('');

      if (centered) {
        document.activeElement?.blur?.();
      }
    };

    const subscribe = async ({ email, phoneNumber = '', source }) => {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          accept: 'application/vnd.api+json',
          'content-type': 'application/vnd.api+json',
          revision
        },
        body: JSON.stringify(buildSubscriptionPayload({ email, phoneNumber, listId, source }))
      });

      const partialFailure = response.headers.get('x-klaviyo-partial-failure') || '';
      if (!response.ok || (phoneNumber && partialFailure.includes('phone_number'))) {
        let detail = '';
        try {
          const payload = await response.json();
          detail = payload?.errors?.[0]?.detail || '';
        } catch (_error) {
          detail = '';
        }
        throw new Error(detail || 'Subscription could not be completed.');
      }
    };

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      event.stopImmediatePropagation();
      event.stopPropagation();

      if (currentStep === 'complete') return;
      setStatus('');

      if (currentStep === 'email') {
        const email = emailInput.value.trim();
        if (!isValidEmail(email)) {
          emailInput.setCustomValidity('Enter a valid email address.');
          emailInput.reportValidity();
          return;
        }

        emailInput.setCustomValidity('');
        setBusy(true);
        try {
          await subscribe({
            email,
            source: 'Daisy Sold Out Topfold - Email'
          });
          submittedEmail = email;
          markEmailSubmitted();
          window.daisyTrackBackInStockSignup?.();
          showSmsStep();
        } catch (_error) {
          setStatus('email could not be submitted. try again.', true);
        } finally {
          setBusy(false);
        }
        return;
      }

      const normalizedPhoneNumber = normalizePhoneNumber(phoneInput.value);
      if (!isValidPhoneNumber(normalizedPhoneNumber)) {
        phoneInput.setCustomValidity('Enter a valid US phone number or include the country code.');
        phoneInput.reportValidity();
        return;
      }

      phoneInput.setCustomValidity('');
      setBusy(true);
      try {
        await subscribe({
          email: submittedEmail,
          phoneNumber: normalizedPhoneNumber,
          source: 'Daisy Sold Out Topfold - SMS'
        });
        showComplete("you're on the restock and sms list", true);
      } catch (_error) {
        setStatus('phone number could not be confirmed. check the number and try again.', true);
      } finally {
        setBusy(false);
      }
    });

    skipSmsButton?.addEventListener('click', () => {
      showComplete("you're on the sold out email list");
    });

    topfold.querySelectorAll('img').forEach((image) => {
      image.draggable = false;
    });

    setScale();
    window.addEventListener('resize', setScale, { passive: true });
    window.visualViewport?.addEventListener('resize', setScale, { passive: true });
  };

  const initializeAll = () => document.querySelectorAll(SELECTOR).forEach(initializeTopfold);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeAll, { once: true });
  } else {
    initializeAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll?.(SELECTOR).forEach(initializeTopfold);
  });
})();
